import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import type { Application, ApplicationWithAccess } from '@electrodavisan/shared';
import { api } from './api';

export const applicationKeys = {
  mine: ['applications', 'mine'] as const,
  admin: ['applications', 'admin'] as const,
};

/** Aplicaciones visibles para el usuario actual (las autorizadas). */
export function useMyApplications() {
  const auth = useAuth();

  return useQuery<Application[]>({
    queryKey: applicationKeys.mine,
    queryFn: () => api.get<Application[]>('/api/applications'),
    enabled: auth.isAuthenticated,
    staleTime: 60_000,
  });
}

/** Catalogo completo, incluidas las inactivas. Solo para administradores. */
export function useAdminApplications(enabled = true) {
  return useQuery<ApplicationWithAccess[]>({
    queryKey: applicationKeys.admin,
    queryFn: () => api.get<ApplicationWithAccess[]>('/api/admin/applications'),
    enabled,
  });
}

export interface ApplicationFormValues {
  name: string;
  slug: string;
  shortDescription?: string | null;
  description?: string | null;
  iconUrl?: string | null;
  launchUrl?: string | null;
  category?: string | null;
  status: string;
  authMode: string;
  oidcClientId?: string | null;
  healthUrl?: string | null;
  sortOrder: number;
}

function invalidate(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: ['applications'] });
  void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
}

export function useCreateApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: ApplicationFormValues) =>
      api.post<ApplicationWithAccess>('/api/admin/applications', values),
    onSuccess: () => invalidate(queryClient),
  });
}

export function useUpdateApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, values }: { id: string; values: Partial<ApplicationFormValues> }) =>
      api.patch<ApplicationWithAccess>(`/api/admin/applications/${id}`, values),
    onSuccess: () => invalidate(queryClient),
  });
}

export function useReorderApplications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (order: Array<{ id: string; sortOrder: number }>) =>
      api.post<ApplicationWithAccess[]>('/api/admin/applications/reorder', { order }),
    onSuccess: () => invalidate(queryClient),
  });
}

export function useGrantUserAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      keycloakUserSub,
      email,
      displayName,
    }: {
      id: string;
      keycloakUserSub: string;
      email?: string | null;
      displayName?: string | null;
    }) =>
      api.post<ApplicationWithAccess>(`/api/admin/applications/${id}/access/users`, {
        keycloakUserSub,
        email: email || null,
        displayName: displayName || null,
      }),
    onSuccess: () => invalidate(queryClient),
  });
}

export function useRevokeUserAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, sub }: { id: string; sub: string }) =>
      api.delete<ApplicationWithAccess>(
        `/api/admin/applications/${id}/access/users/${encodeURIComponent(sub)}`,
      ),
    onSuccess: () => invalidate(queryClient),
  });
}
